import DocumentActionsSection from 'components/common/documents-upload/DocumentActionsSection';
import PageHeader from 'components/common/general/layouts/PageHeader';
import ProposalStatusLayout from 'components/common/proposal/ProposalStatusLayout';
import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import PageFooter from 'components/common/general/layouts/PageFooter';
import { DocumentList } from 'components/common/documents-upload/DocumentsList';
import PostSubmitDocumentsPageStore, {
  PostSubmitDocumentsPageStoreContext,
  usePostSubmitDocumentsPageStore,
} from 'store/pages/post-submit/post-submit-documents/pageStore';
import { useDocumentsUploadUrlParams } from 'global/hooks/useDocumentsUploadUrlParams';
import { useHistory } from 'react-router-dom';
import { CARD_DATA } from 'global/checklists-helpers/ChecklistHelper';
import { DocumentsBodyLayout } from 'components/common/documents-upload/DocumentsBodyLayout';
import { PageWrapperStyled } from './styles';

const PageContent: React.FC = observer(() => {
  const pageStore = usePostSubmitDocumentsPageStore();
  const { periodId } = useDocumentsUploadUrlParams();
  const history = useHistory();

  useEffect(() => {
    void pageStore.loadInitialData();
  }, []);

  const onBack = (): void => {
    history.push(`/post-submit-checklist/${periodId}`);
  };

  const onStepComplete = async (): Promise<void> => {
    await pageStore.completeStep();
    history.push(`/post-submit-checklist/${periodId}`);
  };

  return (
    <PageWrapperStyled>
      <PageHeader
        title={CARD_DATA.POST_SUBMIT_DOCUMENTS.title}
        subtitle={CARD_DATA.POST_SUBMIT_DOCUMENTS.text}
        subtitleMaxWidth="51rem"
        spacing="WITH_ACTION_BELOW"
        onRedirect={onBack}
      />
      <DocumentsBodyLayout
        actionsSection={
          <DocumentActionsSection
            requestDocumentModalStore={pageStore.requestDocumentModalStore}
            uploadDocumentModalStore={pageStore.uploadDocumentModalStore}
            loading={pageStore.isPageLoading}
            disableMutativeActions={pageStore.thisStepHasBeenCompleted}
          />
        }
        documentsList={
          <DocumentList
            activeDocuments={pageStore.activeDocuments}
            inactiveDocuments={pageStore.inactiveDocuments}
            loading={pageStore.isPageLoading}
            disableMutativeActions={pageStore.thisStepHasBeenCompleted}
          />
        }
        uncompleteStep={pageStore.uncompleteStep}
        stepIsCompleted={pageStore.thisStepHasBeenCompleted && !pageStore.isPageLoading}
        hasOneOrMoreDocuments={pageStore.activeDocuments.length > 0}
      />
      <PageFooter
        onComplete={onStepComplete}
        onBack={onBack}
        disableCompleteButton={
          pageStore.thisStepHasBeenCompleted || pageStore.isPageLoading || !pageStore.pageDomainCompletion.allowed
        }
        completeDisabledReason={
          pageStore.thisStepHasBeenCompleted || pageStore.isPageLoading
            ? ''
            : pageStore.pageDomainCompletion.notAllowedMessage
        }
      />
    </PageWrapperStyled>
  );
});

const PostSubmitDocumentsPage: React.FC = () => {
  const { periodId } = useDocumentsUploadUrlParams();

  const pageStore = useMemo(() => new PostSubmitDocumentsPageStore({ entityId: periodId }), []);
  return (
    <ProposalStatusLayout>
      <PostSubmitDocumentsPageStoreContext.Provider value={pageStore}>
        <PageContent />
      </PostSubmitDocumentsPageStoreContext.Provider>
    </ProposalStatusLayout>
  );
};

export default PostSubmitDocumentsPage;
